import {
  ContentData,
  FileExtensions,
  IUploadConfig,
  UploadRequestMetaData,
  UploadScenarios,
} from 'interfaces/file-uploader';
import axios from 'axios';
import pdfDocument from 'assets/images/pdf-document.png';
import powerpointDocument from 'assets/images/powerpoint-document.png';
import excelDocument from 'assets/images/excel-document.png';
import csvDocument from 'assets/images/csv-document.png';
import docPlaceholder from 'assets/images/doc_placeholder.svg';
import { request } from './request';

export enum FileType {
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  FOLDER = 'folder',
  FONT = 'font',
  COMPRESSED_FILE = 'compressed-file',
}

const mbSize = 1000000;

const videos = [
  FileExtensions.mp4,
  FileExtensions.flv,
  FileExtensions.mkv,
  FileExtensions.webm,
  FileExtensions.mov,
  FileExtensions.wmv,
];
const images = [FileExtensions.jpg, FileExtensions.jpeg, FileExtensions.png, FileExtensions.gif];
const documents = [
  FileExtensions.pdf,
  FileExtensions.ppt,
  FileExtensions.pptx,
  FileExtensions.xls,
  FileExtensions.xlsx,
  FileExtensions.csv,
];
const fonts = [FileExtensions.woff, FileExtensions.woff2];
const compressItems = [FileExtensions.zip];

export const uploadConfig = new Map<number, IUploadConfig>([
  [
    UploadScenarios.expertVideo,
    { allowedExtensions: [...videos], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertProfile,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertSponsor,
    { allowedExtensions: [...images], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.expertBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.expertContent,
    {
      allowedExtensions: [...images, ...videos, ...documents],
      sizeLimit: 5120 * mbSize,
      sizeLimitsByType: {
        [FileType.DOCUMENT]: 30 * mbSize,
      },
    },
  ],
  [
    UploadScenarios.sessionHighlightMedia,
    { allowedExtensions: [...images, ...videos], sizeLimit: 500 * mbSize },
  ],
  [
    UploadScenarios.articleBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.sessionBanner,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.sessionFeedback,
    { allowedExtensions: [...videos], sizeLimit: 9000 * mbSize }, // allow 9 GB
  ],
  [
    UploadScenarios.groupBanner,
    { allowedExtensions: [...images], sizeLimit: 100 * mbSize },
  ],
  [
    UploadScenarios.formBadge,
    { allowedExtensions: [...images], sizeLimit: 30 * mbSize },
  ],
  [
    UploadScenarios.generateThumbnail,
    { allowedExtensions: [FileExtensions.jpeg], sizeLimit: 2 * mbSize },
  ],
]);

export const extensionToType = new Map<string, string>([]);
videos.forEach((video) => {
  extensionToType.set(video, FileType.VIDEO);
});
images.forEach((image) => {
  extensionToType.set(image, FileType.IMAGE);
});
documents.forEach((document) => {
  extensionToType.set(document, FileType.DOCUMENT);
});
fonts.forEach((font) => {
  extensionToType.set(font, FileType.FONT);
});
compressItems.forEach((item) => {
  extensionToType.set(item, FileType.COMPRESSED_FILE);
});

/**
 * @param  {any} e  File change event
 * @param  {string} url Current url of the content data object that needs to be deleted
 * @return void
 */
export const handleContentDataDelete = async (
  url: string,
  scenario: UploadScenarios,
): Promise<void> => {
  const content: ContentData = {
    action: 'delete',
    type: '',
    file: '',
    fileName: '',
    url,
    extension: '',
    category: '',
  };
  if (!url || !scenario) {
    return;
  }
  const UPLOAD_PATH: UploadRequestMetaData = { path: '', method: 'POST' };
  UPLOAD_PATH.path = '/expert/upload';
  if (scenario === UploadScenarios.expertVideo) {
    content.type = 'video';
  } else if (
    scenario === UploadScenarios.expertProfile
    || scenario === UploadScenarios.expertSponsor
  ) {
    content.type = 'image';
  } else {
    return;
  }
  await request(UPLOAD_PATH, content, true, true);
};

export const getFileTypeFromUrl = (url: string): string => {
  const extention = (url.split('.').at(-1) as string).toLowerCase();
  return extensionToType.get(`.${extention}`) as string;
};

export const getFileDataFromUrl = async (url: string): Promise<any> => new Promise(
  (resolve, reject) => {
    const fileName = url?.split('/').pop() ?? '';
    const extension = fileName.split('.').pop()?.toLowerCase();

    if (!extension) {
      reject(new Error('URL is invalid - no file extension found'));
      return;
    }

    const extensionWithDot = `.${extension}`;
    const fileCategory = extensionToType.get(extensionWithDot) as string;

    if (!fileCategory) {
      reject(new Error('URL is invalid - unsupported file type'));
      return;
    }

    axios({
      url,
      method: 'head',
      withCredentials: false,
    }).then(() => {
      resolve({
        fileType: extensionWithDot.toUpperCase(),
        fileCategory,
        fileName,
      });
    }).catch(() => {
      reject(new Error('URL is invalid'));
    });
  },
);

export const urltoFile = async (url: string, filename: string, mimeType: string) => (fetch(url)
  .then((res) => res.arrayBuffer())
  .then((buf) => new File([buf], filename, { type: mimeType }))
);

export const urlExists = (url: string) => {
  try {
    const http = new XMLHttpRequest();
    http.open('HEAD', url, false);
    http.send();
    return [200].includes(http.status);
  } catch (error) {
    return false;
  }
};

/**
 * Get the appropriate document icon based on file extension
 * @param fileExtension - File extension (e.g., '.pdf', '.pptx', '.xlsx')
 * @returns Icon source path
 */
export const getDocumentIcon = (fileExtension: string): string => {
  const ext = fileExtension.toLowerCase();
  const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;

  switch (normalizedExt) {
    case '.pdf':
      return pdfDocument;
    case '.ppt':
    case '.pptx':
      return powerpointDocument;
    case '.xls':
    case '.xlsx':
      return excelDocument;
    case '.csv':
      return csvDocument;
    default:
      return docPlaceholder;
  }
};

/**
 * Get document type name for display
 * @param fileExtension - File extension (e.g., '.pdf', '.pptx', '.xlsx')
 * @returns Human readable document type
 */
export const getDocumentTypeName = (fileExtension: string): string => {
  const ext = fileExtension.toLowerCase();
  const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;

  switch (normalizedExt) {
    case '.pdf':
      return 'PDF Document';
    case '.ppt':
      return 'PowerPoint Presentation';
    case '.pptx':
      return 'PowerPoint Presentation';
    case '.xls':
      return 'Excel Spreadsheet';
    case '.xlsx':
      return 'Excel Spreadsheet';
    case '.csv':
      return 'CSV Spreadsheet';
    default:
      return 'Document';
  }
};
